import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Lock, MapPin, Cpu, ArrowRight, Layers, Bot,
  FileSignature, Users, Bell, CheckCircle2, Database, Globe2,
  KeyRound, ScrollText, Quote, Star,
} from "lucide-react";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import ScannerWidget from "../components/marketing/ScannerWidget";
import Pricing from "../components/marketing/Pricing";
import FAQ from "../components/marketing/FAQ";
import CookieConsent from "../components/marketing/CookieConsent";

const HERO_DASHBOARD_IMG = "https://static.prod-images.emergentagent.com/jobs/34cb90a0-ddc9-4d58-8170-e02e719b7164/images/48b9fded141896045edde93bb3e5a88ecabc28901264d0d8064b50872f60e92b.png";
const MOBILE_PORTAL_IMG = "https://static.prod-images.emergentagent.com/jobs/34cb90a0-ddc9-4d58-8170-e02e719b7164/images/3f17485446a7b00a158df2082fd38b709117426900c981fd80fa08e99ddbdd4b.png";
const TABLET_SIGN_IMG = "https://static.prod-images.emergentagent.com/jobs/34cb90a0-ddc9-4d58-8170-e02e719b7164/images/4ed277c4263bfe1518a8760f8615b1d8ddce1c221ad68254e5216777a88eb4b0.png";
const PERMISSION_IMG = "https://static.prod-images.emergentagent.com/jobs/34cb90a0-ddc9-4d58-8170-e02e719b7164/images/ae5e6d34f298e9c39c82b8da6f5312fb1fb35c6f8f248fdb86fc5977c3353ae6.png";
const LOGOS = [
  "https://static.prod-images.emergentagent.com/jobs/34cb90a0-ddc9-4d58-8170-e02e719b7164/images/90f94b9dc68c6f33a71bdf3c7cd5b80b6d45c93a272018fbddfbfbd450eb6281.png",
  "https://static.prod-images.emergentagent.com/jobs/34cb90a0-ddc9-4d58-8170-e02e719b7164/images/f0fb6000b8e24e72e8d529b1830bc9b0c88620f19484610f6fe26911da3ae801.png",
  "https://static.prod-images.emergentagent.com/jobs/34cb90a0-ddc9-4d58-8170-e02e719b7164/images/f9ba5372562bf88ccf4dd300af414b73df4b60b65cf0d81dbe94c732b0eb59c7.png",
];

function TrustPill({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs font-medium text-white/80">
      <Icon size={14} className="text-emerald2" />
      {label}
    </span>
  );
}

function Hero() {
  return (
    <section className="relative bg-navy-900 text-white overflow-hidden pt-32 pb-20 md:pb-28" data-testid="hero">
      <div className="absolute inset-0 hero-grid pointer-events-none" />
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Trust bar */}
        <div className="flex flex-wrap gap-x-8 gap-y-3 mb-10 pb-8 border-b border-white/10">
          <TrustPill icon={ShieldCheck} label="FCA-aware ruleset" />
          <TrustPill icon={Lock} label="GDPR-compliant" />
          <TrustPill icon={MapPin} label="UK data hosting" />
          <TrustPill icon={KeyRound} label="Bank-grade encryption" />
        </div>

        {/* Centred headline + CTAs */}
        <div className="text-center max-w-4xl mx-auto">
          <span className="badge2" style={{ background: "rgba(79,70,229,0.16)", color: "#A5B4FC", borderColor: "rgba(79,70,229,0.4)" }}>
            <Cpu size={12} /> FCA · ASA · ICO RULESETS
          </span>
          <h1 className="t-display mt-5 leading-[1.05]" style={{ fontSize: "clamp(36px, 5.4vw, 64px)" }}>
            Compliant client onboarding.<br />
            <span style={{ color: "#10B981" }}>Done in minutes.</span> Proven forever.
          </h1>
          <p className="mt-6 t-body-lg text-white/70 max-w-2xl mx-auto">
            The only platform combining smart onboarding flows with real-time FCA compliance checking — and an immutable audit trail that proves every decision.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup" className="btn btn-primary btn-lg" data-testid="hero-cta-trial">
              Start 14-day free trial <ArrowRight size={18} />
            </Link>
            <a href="#scanner" className="btn btn-secondary-on-dark btn-lg" data-testid="hero-cta-demo">
              Try the live scanner
            </a>
          </div>
          <p className="mt-4 t-caption" style={{ color: "rgba(255,255,255,0.55)" }}>
            No credit card required · 4-minute setup · Cancel anytime
          </p>
        </div>

        {/* Demo video — sits as a natural extension of the hero, autoplays silently, loops.
            Hidden on small mobile to protect the CTA above the fold. The dashboard image
            acts as poster until the real video file is provided. */}
        <HeroVideo />
      </div>
    </section>
  );
}

function HeroVideo() {
  /* Real video file can be dropped in by setting VIDEO_SRC below — it auto-plays muted
     and loops. Until then the poster renders identically to a paused product window. */
  const VIDEO_SRC = ""; // e.g. "https://cdn.compliche.com/demo.mp4"
  return (
    <div
      className="relative mx-auto mt-12 md:mt-16 hidden sm:block"
      style={{ maxWidth: 1080 }}
      data-testid="hero-video"
    >
      <div
        className="absolute -inset-4 rounded-3xl opacity-60 blur-2xl pointer-events-none"
        style={{ background: "radial-gradient(closest-side, rgba(79,70,229,0.35), transparent)" }}
      />
      {/* Browser-chrome window */}
      <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-navy-800">
        <div className="flex items-center gap-2 px-4 py-3 bg-navy-700/80 border-b border-white/5">
          <span className="w-3 h-3 rounded-full" style={{ background: "#FF5F57" }} />
          <span className="w-3 h-3 rounded-full" style={{ background: "#FEBC2E" }} />
          <span className="w-3 h-3 rounded-full" style={{ background: "#28C840" }} />
          <div className="ml-3 flex-1 max-w-md mx-auto h-6 rounded-md bg-navy-900/70 border border-white/5 flex items-center px-3 t-caption text-white/40 t-mono">
            app.compliche.com/compliance
          </div>
        </div>
        <video
          className="block w-full h-auto bg-navy-900"
          poster={HERO_DASHBOARD_IMG}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          aria-label="Compliche product walkthrough"
          data-testid="hero-video-el"
        >
          {VIDEO_SRC && <source src={VIDEO_SRC} type="video/mp4" />}
        </video>
      </div>
      {/* Floating compliance-score card — kept from the original hero */}
      <div className="absolute -bottom-6 -left-6 card2 p-4 hidden md:flex items-center gap-3 shadow-card-hover" style={{ width: 250 }}>
        <div className="w-10 h-10 rounded-full bg-emerald2-50 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 size={20} className="text-emerald2-dark" />
        </div>
        <div>
          <div className="t-h4">Compliance: 98/100</div>
          <div className="t-caption">Approved · FCA COBS 4.2</div>
        </div>
      </div>
    </div>
  );
}

function SocialProof() {
  return (
    <section className="bg-navy-800 text-white py-14 border-t border-white/5" data-testid="social-proof">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-center t-label text-white/40 mb-8">Trusted by regulated firms across the UK</p>
        <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-6 mb-12 opacity-70">
          {LOGOS.concat(LOGOS).slice(0, 5).map((src, i) => (
            <img key={i} src={src} alt="" className="h-8 md:h-10 brightness-0 invert" style={{ filter: "brightness(0) invert(1) opacity(0.7)" }} />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {[
            { stat: "73,000+", caption: "FCA-regulated firms in the UK" },
            { stat: "£18M+", caption: "Average cost of an FCA enforcement action" },
            { stat: "14 days", caption: "Your free trial starts today — no card required" },
          ].map((s) => (
            <div key={s.caption}>
              <div className="t-mono" style={{ fontSize: 36, fontWeight: 700, color: "#FFFFFF" }}>{s.stat}</div>
              <div className="t-body-sm text-white/60 mt-1">{s.caption}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const STEPS = [
    { icon: Layers, title: "Build your onboarding flow", body: "Create branded client journeys with questionnaires, document requests, and e-signatures. Use templates or build from scratch in minutes." },
    { icon: Bot, title: "Compliche checks everything for compliance", body: "Before anything reaches a client, we scan every word against FCA financial promotions rules, ASA standards, and ICO guidelines." },
    { icon: CheckCircle2, title: "Clients onboard. You're protected.", body: "Your client completes onboarding via a branded link. Every action creates an immutable audit record that proves compliance — forever." },
  ];
  return (
    <section id="how" className="py-24 bg-white" data-testid="how-it-works">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <div className="t-label text-indigo2 mb-3">How it works</div>
          <h2 className="t-display">From signup to compliant in minutes.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10 relative">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative" data-testid={`how-step-${i + 1}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-indigo2 text-white flex items-center justify-center font-bold t-mono">{i + 1}</div>
                <s.icon size={22} className="text-emerald2-dark" />
              </div>
              <h3 className="t-h3 mb-2">{s.title}</h3>
              <p className="t-body text-slate2">{s.body}</p>
              {i < STEPS.length - 1 && (
                <div className="hidden md:block absolute top-5 -right-6 text-slate2"><ArrowRight size={20} /></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureRow({ index, icon: Icon, title, body, img, bullets }) {
  const reverse = index % 2 === 1;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center py-12 md:py-20" data-testid={`feature-${index}`}>
      <div className={`${reverse ? "lg:order-2" : ""}`}>
        <div className="w-12 h-12 rounded-lg bg-indigo2-50 flex items-center justify-center mb-5">
          <Icon size={24} className="text-indigo2" />
        </div>
        <h3 className="t-h1 mb-4">{title}</h3>
        <p className="t-body-lg text-slate2 mb-6">{body}</p>
        {bullets && (
          <ul className="space-y-2">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 t-body-sm">
                <CheckCircle2 size={16} className="text-emerald2 mt-1 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={`${reverse ? "lg:order-1" : ""}`}>
        <div className="card2 p-2 overflow-hidden card2-hover">
          <img src={img} alt={title} className="w-full h-auto rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function Features() {
  return (
    <section id="features" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24">
        <div className="text-center mb-8 max-w-2xl mx-auto">
          <div className="t-label text-indigo2 mb-3">Features</div>
          <h2 className="t-display">Everything a regulated firm needs.</h2>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 divide-y divide-border2">
        <FeatureRow
          index={0}
          icon={Bot}
          title="Catch compliance issues before clients do."
          body="Our compliance engine scans against FCA, ASA, and ICO rules in seconds. Flags specific violations, cites which rule was broken, and suggests a compliant alternative."
          img={HERO_DASHBOARD_IMG}
          bullets={["Real-time scoring 0–100", "Rule-specific citations", "Suggested rewrites", "Override with written reason"]}
        />
        <FeatureRow
          index={1}
          icon={Lock}
          title="A trail no one — including us — can touch."
          body="Every approval, every override, every signature is cryptographically sealed and retained for 7 years. SHA-256 hash-chained. Mathematically impossible to alter."
          img={PERMISSION_IMG}
          bullets={["SHA-256 hash chain", "7-year retention (FCA)", "PDF + CSV export", "Per-event drill-down"]}
        />
        <FeatureRow
          index={2}
          icon={Globe2}
          title="Your brand. Their trust. Our security."
          body="Clients see your firm's logo and colours. They complete onboarding in a clean, mobile-first guided experience that feels like premium financial services."
          img={MOBILE_PORTAL_IMG}
          bullets={["Full white-label client portal", "Mobile-first responsive", "Auto-save every 30s", "Single-use signed links"]}
        />
        <FeatureRow
          index={3}
          icon={FileSignature}
          title="Legally binding signatures in minutes."
          body="Clients read, sign, and return documents without printing anything. Signatures are timestamped, IP-stamped, and cryptographically sealed into the audit trail."
          img={TABLET_SIGN_IMG}
          bullets={["E-signature with IP + timestamp", "Encrypted document storage", "PDF, DOCX, image uploads", "MIME-type + size validation"]}
        />
      </div>
    </section>
  );
}

function SecurityTrust() {
  const ITEMS = [
    { icon: KeyRound, title: "AES-256 at rest", body: "Per-firm encryption keys with 90-day automatic rotation." },
    { icon: Globe2, title: "TLS 1.3 in transit", body: "Reject TLS 1.0/1.1. HSTS preload. Certificate pinning." },
    { icon: MapPin, title: "UK data hosting", body: "AWS London exclusively. No US transfers, no Schrems II risk." },
    { icon: ScrollText, title: "Immutable audit logs", body: "SHA-256 hash chain. 7-year retention. Cannot be altered." },
    { icon: Users, title: "Role-based access", body: "Four firm roles enforced at every API call. Least-privilege default." },
    { icon: Database, title: "GDPR + DPA ready", body: "Default DPA template available. Right-to-export built-in." },
  ];
  return (
    <section id="security" className="bg-navy-900 text-white py-24" data-testid="security-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <div className="t-label mb-3" style={{ color: "#10B981" }}>Security architecture</div>
          <h2 className="t-display">Bank-grade security. FCA-grade compliance.</h2>
          <p className="mt-4 t-body-lg text-white/65">
            Security isn't a feature. It's the entire product.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ITEMS.map((it) => (
            <div
              key={it.title}
              className="p-6 rounded-xl border border-white/10 transition-all hover:bg-white/[0.03] hover:border-emerald2/30"
              style={{ background: "rgba(255,255,255,0.02)" }}
              data-testid={`security-item-${it.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: "rgba(16,185,129,0.12)" }}>
                <it.icon size={20} className="text-emerald2" />
              </div>
              <h3 className="t-h4 mb-2">{it.title}</h3>
              <p className="t-body-sm text-white/60">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const ITEMS = [
    { quote: "Compliche has replaced four separate compliance tools and saved us about 11 hours every week. The audit trail alone makes it worth the price.", firm: "Mid-sized IFA, FCA-regulated", name: "Compliance Director" },
    { quote: "The Compliche scanner caught a financial promotion we'd already approved internally. Genuine 'I can't believe we missed that' moment. We rolled it out firm-wide.", firm: "Mortgage Brokerage, London", name: "Operations Lead" },
    { quote: "I sleep better knowing the audit trail is immutable. We had an FCA Section 166 last year — Compliche would have made it trivial.", firm: "Consumer Credit Firm", name: "MLRO" },
  ];
  return (
    <section className="py-24 bg-surface" data-testid="testimonials">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="t-label text-indigo2 mb-3">Customer love</div>
          <h2 className="t-display">Trusted by UK regulated firms.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {ITEMS.map((t, i) => (
            <div key={i} className="card2 p-8 card2-hover" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1 mb-4 text-amber2">
                {Array.from({ length: 5 }).map((_, j) => <Star key={j} size={16} fill="currentColor" />)}
              </div>
              <Quote size={24} className="text-indigo2 opacity-50 mb-3" />
              <p className="t-body mb-6">"{t.quote}"</p>
              <div className="pt-4 border-t border-border2">
                <div className="t-h4">{t.name}</div>
                <div className="t-caption">{t.firm}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ScannerSection() {
  return (
    <section id="scanner" className="bg-surface py-24">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <div className="t-label text-indigo2 mb-3">Try it free · no signup</div>
          <h2 className="t-display">See how your copy holds up.</h2>
          <p className="mt-4 t-body-lg text-slate2">
            Paste any marketing copy or client communication. Get an FCA-aware compliance verdict in seconds.
          </p>
        </div>
        <ScannerWidget />
      </div>
    </section>
  );
}

function BlogPreview() {
  const POSTS = [
    { tag: "Consumer Duty", title: "What the Consumer Duty means for your client onboarding in 2026", excerpt: "A practical breakdown of how Consumer Duty reshapes onboarding flows for IFAs, brokers and consumer credit firms.", date: "Feb 12, 2026" },
    { tag: "Financial Promotions", title: "5 FCA financial promotions mistakes regulated firms make every week", excerpt: "We analysed 10,000 anonymised compliance scans and found these five patterns are responsible for 73% of all warnings.", date: "Jan 28, 2026" },
    { tag: "Audit Trail", title: "Why your current onboarding process may not survive an FCA audit", excerpt: "What FCA supervisors actually look for during a Section 166 review — and how to prove your decisions retroactively.", date: "Jan 14, 2026" },
  ];
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="t-label text-indigo2 mb-3">Resources</div>
            <h2 className="t-display">FCA compliance insights.</h2>
          </div>
          <a href="#" className="hidden md:inline-flex btn btn-secondary">View all <ArrowRight size={16} /></a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {POSTS.map((p, i) => (
            <article key={i} className="card2 card2-hover overflow-hidden" data-testid={`blog-post-${i}`}>
              <div
                className="h-40 bg-gradient-to-br"
                style={{
                  background: i % 3 === 0
                    ? "linear-gradient(135deg, #4F46E5 0%, #0F1C2E 100%)"
                    : i % 3 === 1
                    ? "linear-gradient(135deg, #10B981 0%, #0F1C2E 100%)"
                    : "linear-gradient(135deg, #F59E0B 0%, #0F1C2E 100%)",
                }}
              />
              <div className="p-6">
                <span className="badge2 badge-locked">{p.tag}</span>
                <h3 className="t-h4 mt-3 mb-2">{p.title}</h3>
                <p className="t-body-sm text-slate2 mb-4">{p.excerpt}</p>
                <div className="flex items-center justify-between">
                  <span className="t-caption">{p.date}</span>
                  <a href="#" className="text-indigo2 t-body-sm font-medium hover:underline">Read more →</a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-24 bg-navy-900 text-white relative overflow-hidden" data-testid="final-cta">
      <div className="absolute inset-0 hero-grid opacity-50" />
      <div className="absolute inset-0 hero-glow opacity-60" />
      <div className="relative max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <Bell size={32} className="text-emerald2 mx-auto mb-4 animate-lock-pulse" />
        <h2 className="t-display mb-4">Your next FCA review is closer than you think.</h2>
        <p className="t-body-lg text-white/70 mb-8">
          Start your free 14-day trial. Build your first compliant onboarding flow in under an hour. Sleep better tonight.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/signup" className="btn btn-primary btn-lg">Start free trial <ArrowRight size={18} /></Link>
          <a href="mailto:hello@compliche.com" className="btn btn-secondary-on-dark btn-lg">Book a demo</a>
        </div>
        <p className="mt-5 t-caption text-white/50">No credit card required · 4-minute setup · Cancel anytime</p>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="bg-white">
      <MarketingNav />
      <Hero />
      <SocialProof />
      <HowItWorks />
      <Features />
      <ScannerSection />
      <SecurityTrust />
      <Testimonials />
      <Pricing />
      <FAQ />
      <BlogPreview />
      <FinalCTA />
      <MarketingFooter />
      <CookieConsent />
    </div>
  );
}
